import {gql} from '@apollo/client';
import {getTestProps} from '@benchling/js-test-utils';
import {Accordion, Group, Stack, createStyles} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {ChatRenderContext} from 'llm/chat-library/ChatRenderContext';
import {ProgressToolCall, ToolCallStatus} from 'llm/chat-library/progress/ProgressTypes';
import {
  SkillFileContentRenderer,
  MarkdownBlock,
  MonoBlock,
  ReadOnlyCodeBlock,
} from 'llm/chat-library/shared/SkillFileContentBlocks';
import {BINARY_FILE_PREVIEW_MESSAGE} from 'llm/chat-library/shared/SkillFileUtils';
import {
  MemoryToolChatSessionRoutineQuery,
  MemoryToolChatSessionRoutineQueryVariables,
} from 'llm/chat-library/tool-details/__generated/MemoryToolChatSessionRoutineQuery';
import {CouncilResponse, parseCouncilToolResponse} from 'llm/chat-library/tool-details/CouncilToolCallParser';
import CreateDashboardFromQueryButton, {
  canUserCreateOrEditInsightsDashboards,
} from 'llm/chat-library/tool-details/CreateDashboardFromQueryButton';
import {
  formatLoadSkillInput,
  isNativeLoadSkill,
  parseLoadSkillResponse,
} from 'llm/chat-library/tool-details/LoadSkillToolCallParser';
import {
  formatManageSkillInput,
  getManageSkillFiles,
  ParsedSkillResponse,
  parseManageSkillResponse,
} from 'llm/chat-library/tool-details/ManageSkillToolCallParser';
import {MemoryVersionLink} from 'llm/chat-library/tool-details/MemoryToolResults';
import {
  MemoryToolCall,
  ParsedProgressToolCall,
  SearchChemicalsToolCall,
  SearchNotebookEntriesToolCall,
  MCPToolCall,
  parseProgressToolCall,
} from 'llm/chat-library/tool-details/ParsedProgressToolCall';
import {getToolIntentSummary} from 'llm/chat-library/tool-details/toolIntentSummary';
import {ToolType, getSleepDurationLabel, isMCPTool} from 'llm/chat-library/tool-details/types';
import {ResolvedSkillFile, useResolvedSkillFiles} from 'llm/chat-library/tool-details/useResolvedSkillFiles';
import {WebSearchResults} from 'llm/chat-library/tool-details/WebSearchResults';
import {parseWebSearchResults} from 'llm/chat-library/tool-details/WebSearchToolCallParser';
import {errorIndicatorAttrs, loadingIndicatorAttrs} from 'notebook/util/ExportInstrumentation';
import {
  FONT_PROXIMA_12,
  FONT_PROXIMA_14,
  FONT_WEIGHT_PROXIMA_REGULAR,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import BenchChip, {isModelBenchChipSupported} from 'ui-library/chips/BenchChip';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {GRAY_DARKER} from 'ui-library/Colors';
import {CenteredSpinner} from 'ui-library/Spinner';
import {apiIdToIconName} from 'util/APIUtil';
import {assertNever} from 'util/Types';

// ============================================================================
// Skill file list rendering
// ============================================================================

const useSkillFileStyles = createStyles((theme) => ({
  fileList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.md,
  },
  fileBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
  },
  filePath: {
    ...FONT_PROXIMA_12,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: theme.colors.gray[7],
    fontFamily: theme.fontFamilyMonospace,
  },
  fileBody: {
    flex: 'none',
  },
}));

function SkillFileBody({file}: {file: ResolvedSkillFile}): JSX.Element {
  switch (file.status) {
    case 'loading':
      return (
        <div {...loadingIndicatorAttrs()}>
          <CenteredSpinner />
        </div>
      );
    case 'binary':
      return <MonoBlock text={BINARY_FILE_PREVIEW_MESSAGE} />;
    case 'error':
      return (
        <div {...errorIndicatorAttrs()}>
          <MonoBlock text={`Could not load file content from artifact "${file.artifactId ?? file.path}".`} />
        </div>
      );
    case 'inline':
    case 'resolved':
      return <SkillFileContentRenderer virtualPath={file.path} content={file.content} />;
    default:
      assertNever(file.status);
      return <MonoBlock text="No file contents available" />;
  }
}

function FileContentBlock({file}: {file: ResolvedSkillFile}): JSX.Element {
  const {classes} = useSkillFileStyles();
  return (
    <div className={classes.fileBlock}>
      <div className={classes.filePath}>{file.path}</div>
      <div className={classes.fileBody}>
        <SkillFileBody file={file} />
      </div>
    </div>
  );
}

/**
 * Render the list of skill files. When a file's content comes from an artifact (ManageSkill inputs
 * may pass content by artifact id), text files are resolved so the preview matches inline content
 * and binary files show a placeholder; `chatSessionId` is required to resolve artifact ids and is
 * omitted for content that is already inline text (e.g. LoadSkill output).
 */
function SkillFileList({
  parsed,
  chatSessionId,
}: {
  parsed: ParsedSkillResponse;
  chatSessionId?: string;
}): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useSkillFileStyles();
  const resolvedFiles = useResolvedSkillFiles(parsed.files, chatSessionId);
  return (
    <div className={classes.fileList}>
      {resolvedFiles.map((file) => (
        <FileContentBlock key={file.path} file={file} />
      ))}
    </div>
  );
}

const useCouncilResponseStyles = createStyles((theme) => ({
  control: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: theme.colors.gray[7],
  },
  panel: {
    paddingTop: theme.spacing.xs,
  },
}));

interface DisplayedCouncilResponse extends CouncilResponse {
  label: string;
}

const COUNCIL_RESPONSE_WRAPPER_REGEX = /<council_response(?:\s|>)/;

function getDisplayedCouncilResponses(responses: Array<CouncilResponse>): Array<DisplayedCouncilResponse> {
  const providerCounts = new Map<string, number>();
  responses.forEach((response) => {
    providerCounts.set(response.provider, (providerCounts.get(response.provider) ?? 0) + 1);
  });

  const providerOccurrences = new Map<string, number>();
  return responses.map((response) => {
    const providerCount = providerCounts.get(response.provider) ?? 0;
    if (providerCount <= 1) {
      return {...response, label: response.provider};
    }

    const occurrence = (providerOccurrences.get(response.provider) ?? 0) + 1;
    providerOccurrences.set(response.provider, occurrence);
    return {...response, label: `${response.provider} (${occurrence})`};
  });
}

function getEmptyCouncilResponseText(rawResponse: string): string {
  if (COUNCIL_RESPONSE_WRAPPER_REGEX.test(rawResponse)) {
    return 'No response available';
  }
  return rawResponse || 'No response available';
}

function CouncilResponseList({responses}: {responses: Array<CouncilResponse>}): JSX.Element {
  const {classes} = useCouncilResponseStyles();
  const {isExportRender} = ChatRenderContext.useContext();
  const displayedResponses = getDisplayedCouncilResponses(responses);
  const accordionValues = displayedResponses.map((response, index) => `${response.label}-${index}`);
  return (
    <Accordion
      multiple={true}
      defaultValue={isExportRender ? accordionValues : []}
      classNames={{control: classes.control, panel: classes.panel}}
    >
      {displayedResponses.map((response, index) => (
        <Accordion.Item key={`${response.label}-${index}`} value={`${response.label}-${index}`}>
          <Accordion.Control
            {...getTestProps({
              component: 'CouncilResponseList',
              element: 'responseControl',
              index: String(index),
            })}
          >
            {response.label}
          </Accordion.Control>
          <Accordion.Panel
            {...getTestProps({
              component: 'CouncilResponseList',
              element: 'responsePanel',
              index: String(index),
            })}
          >
            <MarkdownBlock markdown={response.content} />
          </Accordion.Panel>
        </Accordion.Item>
      ))}
    </Accordion>
  );
}

// ============================================================================
// Tool input/output formatting
// ============================================================================

// Args can carry arbitrary user content (full document bodies, table cell values, etc.) with no
// upstream size limit. Cap the rendered fallback so one huge write-tool call can't balloon the
// activity modal; the raw data is unaffected, only how much of it we render inline.
const MAX_FALLBACK_ARGS_DISPLAY_LENGTH = 20_000;

function formatArgsForDisplay(args: Record<string, unknown>, forExport = false): string {
  const formatted = Object.entries(args)
    .map(([key, value]) => {
      if (typeof value === 'object' && value != null) {
        return `${key}: ${JSON.stringify(value, null, 2)}`;
      }
      return `${key}: ${value}`;
    })
    .join('\n');
  if (forExport || formatted.length <= MAX_FALLBACK_ARGS_DISPLAY_LENGTH) {
    return formatted;
  }
  const omittedCharCount = formatted.length - MAX_FALLBACK_ARGS_DISPLAY_LENGTH;
  return `${formatted.slice(0, MAX_FALLBACK_ARGS_DISPLAY_LENGTH)}\n\n... (truncated, ${omittedCharCount.toLocaleString()} more characters not shown)`;
}

function formatSearchNotebookEntriesInput(toolCall: SearchNotebookEntriesToolCall): string {
  const parts: Array<string> = [];

  const queries = Array.isArray(toolCall.queries) ? toolCall.queries : [];
  if (queries.length) {
    parts.push(`Queries: ${queries.map((q) => `"${q}"`).join(', ')}`);
  }

  const fields: Array<[string | undefined, string]> = [
    [toolCall.authoredBy?.join(', '), 'Authored By'],
    [toolCall.createdAfter, 'Created After'],
    [toolCall.createdBefore, 'Created Before'],
    [toolCall.modifiedAfter, 'Modified After'],
    [toolCall.modifiedBefore, 'Modified Before'],
    [toolCall.reviewCompletedAfter, 'Review Completed After'],
    [toolCall.reviewCompletedBefore, 'Review Completed Before'],
    [toolCall.reviewStatus, 'Review Status'],
    [toolCall.reviewRole, 'Review Role'],
    [toolCall.inProjectOrFolder, 'In Project/Folder'],
    [toolCall.inStudy, 'In Study'],
  ];

  for (const [value, label] of fields) {
    if (value) {
      parts.push(`${label}: ${value}`);
    }
  }

  if (toolCall.offset !== undefined && toolCall.offset > 0) {
    parts.push(`Offset: ${toolCall.offset}`);
  }

  return parts.length > 0 ? parts.join('\n') : 'No search parameters';
}

function formatSearchChemicalsInput(toolCall: SearchChemicalsToolCall): string {
  const parts: Array<string> = [];
  if (toolCall.query) {
    parts.push(`Query: ${toolCall.query}`);
  }
  if (toolCall.searchMode) {
    parts.push(`Mode: ${toolCall.searchMode}`);
  }
  if (toolCall.structure) {
    parts.push(`Structure: ${toolCall.structure}`);
  }
  return parts.length > 0 ? parts.join('\n') : 'N/A';
}

function isMCPToolCall(parsed: ParsedProgressToolCall): parsed is MCPToolCall {
  return isMCPTool(parsed.tool);
}

const useIdChipListStyles = createStyles((theme) => ({
  chipList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing.xs,
  },
  // Stacks the chip list above the optional "Include Relevant Items" note for IdsToModelsTool.
  idsToModelsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
  },
}));

/**
 * Render a list of Benchling API IDs as entity chips for the activity modal. Each ID self-fetches
 * its name/icon/link via BenchChip; IDs of unsupported types fall back to a plain icon+text chip so
 * nothing is dropped. Renders a fallback message when there are no IDs.
 */
function IdChipList({ids}: {ids: ReadonlyArray<string>}): JSX.Element {
  const {classes} = useIdChipListStyles();
  const validIds = ids.filter((id) => Boolean(id.trim()));
  if (validIds.length === 0) {
    return <MonoBlock text="No IDs provided" />;
  }
  return (
    <div className={classes.chipList}>
      {validIds.map((id) =>
        isModelBenchChipSupported(id) ? (
          <BenchChip key={id} size={ChipSize.MINI} id={id} />
        ) : (
          <Chip key={id} size={ChipSize.MINI} text={id} icon={apiIdToIconName(id)} />
        )
      )}
    </div>
  );
}

/**
 * Activity-modal input rendering for IdsToModelsTool: the looked-up objects as chips, plus the
 * optional "Include Relevant Items" flag when set.
 */
function IdsToModelsInputBlock({
  objectIds,
  includeRelevantItems,
}: {
  objectIds: ReadonlyArray<string>;
  includeRelevantItems?: boolean;
}): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useIdChipListStyles();
  return (
    <div className={classes.idsToModelsContainer}>
      <IdChipList ids={objectIds} />
      {includeRelevantItems ? <MonoBlock text="Include Relevant Items: true" /> : null}
    </div>
  );
}

/**
 * Returns structured input rendering for a tool, or null if the tool has no
 * structured input (in which case the caller should render a fallback).
 *
 * Also used by ToolCallItem in ChatProgress to determine whether a tool
 * has structured detail worth showing in the activity modal.
 */
export function getToolInputBlock(
  toolCall: ProgressToolCall,
  parsed: ParsedProgressToolCall | null,
  forExport = false
): JSX.Element | null {
  if (parsed == null) {
    return null;
  }

  if (isMCPToolCall(parsed)) {
    if (!parsed.args || Object.keys(parsed.args).length === 0) {
      return null;
    }
    return <MonoBlock text={formatArgsForDisplay(parsed.args, forExport)} />;
  }

  switch (parsed.tool) {
    case ToolType.SearchTool:
      return (
        <MonoBlock
          text={`Query: "${parsed.query || ''}"${parsed.objectTypes?.length ? `\nObject Types: ${parsed.objectTypes.join(', ')}` : ''}`}
        />
      );
    case ToolType.SearchNotebookEntries:
      return <MonoBlock text={formatSearchNotebookEntriesInput(parsed)} />;
    case ToolType.SearchSequencesTool:
    case ToolType.QueryWarehouseTool:
      return <ReadOnlyCodeBlock code={parsed.query || ''} language="pgsql" />;
    case ToolType.SearchChemicalsTool:
      return <MonoBlock text={formatSearchChemicalsInput(parsed)} />;
    case ToolType.GetWarehouseOverviewTool:
      return fallbackInputBlock(toolCall);
    case ToolType.SummarizeDocument:
      return parsed.documentId ? <IdChipList ids={[parsed.documentId]} /> : <MonoBlock text="N/A" />;
    case ToolType.AttachmentTool:
      return <MonoBlock text={`Attachment ID: ${parsed.attachmentId || 'N/A'}`} />;
    case ToolType.ReadBlob:
      return <MonoBlock text={`Blob ID: ${parsed.blobId || 'N/A'}`} />;
    case ToolType.PublicDataSubAgent:
    case ToolType.WebSearch:
      return <MonoBlock text={`Query: "${parsed.query || ''}"`} />;
    case ToolType.ResearchSubAgent:
      return <MonoBlock text={`Prompt: "${parsed.prompt || ''}"`} />;
    case ToolType.CouncilTool:
      return <MonoBlock text={`Prompt: "${parsed.prompt || ''}"`} />;
    case ToolType.IdsToModelsTool:
      return (
        <IdsToModelsInputBlock
          objectIds={parsed.objectIds ?? []}
          includeRelevantItems={parsed.includeRelevantItems}
        />
      );
    case ToolType.PlottingTool:
      return <ReadOnlyCodeBlock code={parsed.code || 'No code provided'} language="python" />;
    case ToolType.GenerateImageTool:
      return <MonoBlock text={`Prompt: "${parsed.prompt || ''}"`} />;
    case ToolType.CustomCodeTool:
    case ToolType.ExecuteCustomCodePipelineStep:
    case ToolType.RunCodeTool:
      return <ReadOnlyCodeBlock code={parsed.code || 'No code provided'} language="python" />;
    case ToolType.LoadSkill:
      if (isNativeLoadSkill(parsed.skillId)) {
        return null;
      }
      return <MonoBlock text={formatLoadSkillInput(parsed)} />;
    case ToolType.ManageSkill:
      return <MonoBlock text={formatManageSkillInput(parsed)} />;

    case ToolType.ToolSearch:
      return <MonoBlock text={`Query: "${parsed.query || ''}"`} />;
    case ToolType.SleepTool:
      return <MonoBlock text={`Duration: ${getSleepDurationLabel(toolCall.args) ?? 'N/A'}`} />;

    case ToolType.CreateAnalysisPlan:
    case ToolType.CreateProcedureRunPlan:
    case ToolType.ValidateProcedureRunPlan:
    case ToolType.PromoteProcedureRunPlan:
      return null;

    case ToolType.CreateMemoryFile:
    case ToolType.UpdateMemoryFile:
    case ToolType.ArchiveMemoryFile:
    case ToolType.UnarchiveMemoryFile:
    case ToolType.ListMemoryFiles:
    case ToolType.ReadMemoryFiles:
    case ToolType.SearchMemory:
      return null;

    // TODO: Add structured input rendering for Think in the activity modal.
    case ToolType.Think:
      return null;

    // N/A - these tools are not found in TOOLS_TO_DISPLAY.
    case ToolType.CompleteAnswer:
    case ToolType.CompleteAnswerTool:
    case ToolType.DeepResearchTool:
      return null;
    default:
      assertNever(parsed);
      return null;
  }
}

/**
 * Returns structured output rendering for a tool, or null if the tool has no
 * structured output (in which case the caller should render a fallback).
 */
export function getToolOutputBlock(
  toolCall: ProgressToolCall,
  parsed: ParsedProgressToolCall | null,
  chatSessionId?: string
): JSX.Element | null {
  if (parsed == null) {
    return null;
  }

  if (isMCPToolCall(parsed)) {
    return null;
  }

  switch (parsed.tool) {
    case ToolType.QueryWarehouseTool:
      return <MarkdownBlock markdown={toolCall.responseText?.trim() || 'No response available'} />;
    case ToolType.GetWarehouseOverviewTool:
      return fallbackOutputBlock(toolCall);

    case ToolType.LoadSkill: {
      if (isNativeLoadSkill(parsed.skillId)) {
        return null;
      }
      const rawResponse = toolCall.responseText?.trim() || 'No skill content available';
      // responseText is XML-wrapped by the backend (see load_skill_tool.py).
      // Parse it into the unified skill-response shape so we can render using
      // the same SkillFileList pipeline as ManageSkill (SKILL.md frontmatter
      // is extracted into a structured name/description header by SkillMdBlock).
      const parsedResponse = parseLoadSkillResponse(rawResponse, parsed);
      if (!parsedResponse || !parsedResponse.success) {
        return <MonoBlock text={rawResponse} />;
      }
      return <SkillFileList parsed={parsedResponse} />;
    }

    case ToolType.ManageSkill: {
      const rawResponse = toolCall.responseText?.trim();
      const parsedResponse = rawResponse ? parseManageSkillResponse(rawResponse, parsed) : null;
      if (parsedResponse && !parsedResponse.success) {
        return <MonoBlock text={`Error: ${parsedResponse.message}`} />;
      }
      // Fall back to rendering just the input args when the response isn't
      // parseable (e.g. the tool is still pending or the backend wrapper is
      // missing) — users still see what the agent was trying to write.
      const renderable: ParsedSkillResponse = parsedResponse ?? {
        success: true,
        skillId: null,
        skillName: null,
        files: getManageSkillFiles(parsed),
        message: '',
      };
      if (renderable.files.length === 0) {
        return <MonoBlock text="No file contents available" />;
      }
      return <SkillFileList parsed={renderable} chatSessionId={chatSessionId} />;
    }

    case ToolType.RunCodeTool: {
      const raw = toolCall.responseText?.trim() || '';
      const hasChart = raw.includes('<bnch_chart>');
      const chartWarnings = [...raw.matchAll(/Warning: failed to save chart\(s\): [^\n]*/g)].map(
        (match) => match[0]
      );
      const cleaned = raw
        .replace(/Charts created \(embed these in your response using the tags below\):\n?/g, '')
        .replace(/<bnch_chart>[^<]*<\/bnch_chart>\n?/g, '')
        .replace(/Warning: failed to save chart\(s\): [^\n]*\n?/g, '')
        .trim();
      const output = [cleaned, ...chartWarnings]
        .filter((s) => s)
        .join('\n')
        .trim();
      if (!output && hasChart) {
        return <MonoBlock text="Chart created successfully" />;
      }
      if (!output) {
        return <MonoBlock text={raw || 'No response available'} />;
      }
      return <MonoBlock text={output} />;
    }

    case ToolType.WebSearch: {
      // The backend returns a JSON blob of search results; render them as
      // clickable source cards. Fall back to the raw text (e.g. a "no results"
      // or error message) when it isn't the expected JSON shape.
      const results = parseWebSearchResults(toolCall.responseText ?? '');
      if (results == null) {
        return null;
      }
      return <WebSearchResults results={results} />;
    }

    case ToolType.ToolSearch:
      // The output is an internal <functions> schema dump (ugly JSON, not user-facing). The loaded
      // tools are conveyed by the title, so the Output section is omitted entirely (see ToolDetail).
      return null;
    case ToolType.SleepTool:
      return null;

    case ToolType.CreateAnalysisPlan:
      return <MarkdownBlock markdown={toolCall.responseText?.trim() || 'No response available'} />;
    case ToolType.ResearchSubAgent:
      return <MarkdownBlock markdown={toolCall.responseText?.trim() || 'No response available'} />;
    case ToolType.CouncilTool: {
      const rawResponse = toolCall.responseText?.trim() || '';
      const responses = parseCouncilToolResponse(rawResponse);
      if (responses.length === 0) {
        return <MonoBlock text={getEmptyCouncilResponseText(rawResponse)} />;
      }
      return <CouncilResponseList responses={responses} />;
    }

    case ToolType.CreateProcedureRunPlan:
    case ToolType.ValidateProcedureRunPlan:
    case ToolType.PromoteProcedureRunPlan:
      return null;

    case ToolType.CreateMemoryFile:
    case ToolType.UpdateMemoryFile:
    case ToolType.ArchiveMemoryFile:
    case ToolType.UnarchiveMemoryFile:
    case ToolType.ListMemoryFiles:
    case ToolType.ReadMemoryFiles:
    case ToolType.SearchMemory:
      return <MemoryToolOutput parsed={parsed} chatSessionId={chatSessionId} />;

    // TODO: Add structured output rendering for these displayed tools.
    case ToolType.SearchTool:
    case ToolType.SummarizeDocument:
    case ToolType.SearchNotebookEntries:
    case ToolType.SearchSequencesTool:
    case ToolType.SearchChemicalsTool:
    case ToolType.AttachmentTool:
    case ToolType.ReadBlob:
    case ToolType.PublicDataSubAgent:
    case ToolType.IdsToModelsTool:
    case ToolType.PlottingTool:
    case ToolType.GenerateImageTool:
    case ToolType.CustomCodeTool:
    case ToolType.ExecuteCustomCodePipelineStep:
    case ToolType.Think:
      return null;

    // N/A - these tools are not found in TOOLS_TO_DISPLAY.
    case ToolType.CompleteAnswer:
    case ToolType.CompleteAnswerTool:
    case ToolType.DeepResearchTool:
      return null;
    default:
      assertNever(parsed);
      return null;
  }
}

function fallbackInputBlock(toolCall: ProgressToolCall, forExport = false): JSX.Element {
  const args = toolCall.args ?? {};
  const text = Object.keys(args).length > 0 ? formatArgsForDisplay(args, forExport) : 'No input parameters';
  return <MonoBlock text={text} />;
}

function fallbackOutputBlock(toolCall: ProgressToolCall): JSX.Element {
  const text = toolCall.responseText?.trim();
  if (!text && toolCall.status !== ToolCallStatus.COMPLETED) {
    return <MonoBlock text="Waiting for response..." />;
  }
  return <MonoBlock text={text || 'No response available'} />;
}

export const MEMORY_TOOL_CHAT_SESSION_ROUTINE_QUERY = gql`
  query MemoryToolChatSessionRoutineQuery($chatSessionId: StrictString!) @engComponent(name: LLM) @skipAudit {
    chatSession(id: $chatSessionId) {
      __typename
      ... on ChatSessionNode {
        id
        routineExecution {
          __typename
          ... on RoutineExecutionNode {
            id
            routine {
              id
            }
          }
        }
      }
    }
  }
`;

/**
 * Output rendering for memory tools: the raw output, led by a deep link when the tool referenced a
 * file in a real set version and the owning agent resolves.
 */
function MemoryToolOutput({
  parsed,
  chatSessionId,
}: {
  parsed: MemoryToolCall;
  chatSessionId?: string;
}): JSX.Element {
  const {isExportRender} = ChatRenderContext.useContext();
  const firstPath = parsed.paths[0];
  // The activity modal mounts outside the chat's React context, so resolve the owning routine from
  // the session rather than reading it from context.
  const routineResult = useSkippableBenchlingQuery<
    MemoryToolChatSessionRoutineQuery,
    MemoryToolChatSessionRoutineQueryVariables
  >(MEMORY_TOOL_CHAT_SESSION_ROUTINE_QUERY, {
    variables: {chatSessionId: chatSessionId ?? ''},
    skip: chatSessionId == null || parsed.versionId == null || firstPath == null,
    fetchPolicy: 'cache-first',
  });
  const chatSession = routineResult.type === QueryResultType.SUCCESS ? routineResult.data.chatSession : null;
  const routineExecution =
    chatSession?.__typename === 'ChatSessionNode' ? chatSession.routineExecution : null;
  const routineId =
    routineExecution?.__typename === 'RoutineExecutionNode' ? routineExecution.routine.id : null;
  return (
    <Stack spacing="sm">
      {!isExportRender && routineId != null && parsed.versionId != null && firstPath != null && (
        <MemoryVersionLink page={firstPath} routineId={routineId} versionId={parsed.versionId} />
      )}
      {fallbackOutputBlock(parsed)}
    </Stack>
  );
}

/**
 * Whether the raw-JSON fallback (see fallbackInputBlock/fallbackOutputBlock above) has anything
 * real to show for this tool call. Used by ChatProgress to decide whether a tool with no
 * structured parser should still be clickable to open the activity modal: many write tools (e.g.
 * RegisterEntities, UpdateDocumentContent) have no bespoke renderer above and would otherwise be
 * silently unclickable, leaving their args/response impossible to view even though the fallback
 * blocks already know how to render them. Returns false when there is genuinely nothing to show,
 * so a click can't land on a modal that says "No input parameters" / "No response available" for
 * both sections.
 */
export function hasFallbackToolDetail(toolCall: ProgressToolCall): boolean {
  const hasArgs = toolCall.args != null && Object.keys(toolCall.args).length > 0;
  const hasResponseText = Boolean(toolCall.responseText?.trim());
  return hasArgs || hasResponseText;
}

// ============================================================================
// ToolDetail component
// ============================================================================

export interface ToolDetailProps {
  toolCall: ProgressToolCall;
  /**
   * The chat session the tool call belongs to, used to resolve artifact-backed inputs (e.g.
   * ManageSkill content passed by artifact id) for the preview.
   */
  chatSessionId: string;
}

function getToolOutputActions(parsed: ParsedProgressToolCall | null): JSX.Element | null {
  if (parsed == null || isMCPToolCall(parsed)) {
    return null;
  }
  if (
    parsed.tool === ToolType.QueryWarehouseTool &&
    parsed.query &&
    canUserCreateOrEditInsightsDashboards()
  ) {
    return <CreateDashboardFromQueryButton sqlQuery={parsed.query} />;
  }
  return null;
}

/**
 * Displays detailed information about a specific tool call.
 * Parses the generic ProgressToolCall into typed fields at this boundary
 * so that input/output formatting can use well-typed properties.
 */

export function ToolDetail({toolCall, chatSessionId}: ToolDetailProps): JSX.Element {
  const {isExportRender} = ChatRenderContext.useContext();
  const parsed = parseProgressToolCall(toolCall);
  const outputActions = isExportRender ? null : getToolOutputActions(parsed);
  // The tool's display name now lives in the modal title (see ActivityModal); the body leads with the
  // model-authored intent summary instead, when present.
  const intentSummary = getToolIntentSummary(toolCall);
  const hideLoadSkillDetail = parsed?.tool === ToolType.LoadSkill && isNativeLoadSkill(parsed.skillId);
  // ToolSearch's output is an internal <functions> schema dump; omit the Output section entirely
  // rather than showing the ugly JSON.
  const hideOutputSection = parsed?.tool === ToolType.ToolSearch;

  return (
    <div {...getTestProps({component: 'ToolDetail', element: 'root'})} className={css(styles.container)}>
      {intentSummary && <div className={css(styles.intentSummary)}>{intentSummary}</div>}

      {!hideLoadSkillDetail && (
        <div className={css(styles.content)}>
          <div className={css(styles.section)}>
            <div className={css(styles.sectionLabel)}>Input:</div>
            <div className={css(styles.inputOutputBlock)}>
              {getToolInputBlock(toolCall, parsed, isExportRender) ??
                fallbackInputBlock(toolCall, isExportRender)}
            </div>
          </div>
          {!hideOutputSection && (
            <div className={css(styles.section)}>
              <Group position="apart" spacing="md">
                <div className={css(styles.sectionLabel)}>Output:</div>
                {outputActions && <Group spacing="sm">{outputActions}</Group>}
              </Group>
              <div className={css(styles.inputOutputBlock)}>
                {getToolOutputBlock(toolCall, parsed, chatSessionId) ?? fallbackOutputBlock(toolCall)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '4px',
  },

  intentSummary: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: GRAY_DARKER,
    flex: 'none',
  },

  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    flex: 'none',
  },

  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 'none',
  },

  sectionLabel: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: GRAY_DARKER,
    flex: 'none',
  },

  inputOutputBlock: {
    flex: 'none',
  },
});
